(() => {
  const isMobileGallery = () => window.matchMedia('(max-width: 1024px)').matches;

  const updateHeaderCartCount = (itemCount) => {
    const count = Math.max(0, Number(itemCount) || 0);
    document.querySelectorAll('[data-babaa-cart-count]').forEach((node) => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
    document.querySelectorAll('[data-babaa-cart-link]').forEach((link) => {
      const label = link.getAttribute('aria-label') || '';
      const base = label.replace(/\s*\(\d+\)\s*$/, '').trim();
      link.setAttribute('aria-label', `${base} (${count})`);
    });
    document.dispatchEvent(new CustomEvent('babaa:cart:updated', { detail: { itemCount: count } }));
  };

  const bindAjaxAddToCart = (product) => {
    const form = product.querySelector('form.babaa-product__form');
    if (!form || form.dataset.ajaxCartBound) return;
    form.dataset.ajaxCartBound = 'true';

    const cartAddUrl = product.dataset.cartAddUrl;
    const cartUrl = product.dataset.cartUrl;
    const mainSubmit = form.querySelector('[data-add-to-basket]');
    const mobileSubmit = product.querySelector('[data-mobile-purchase]');
    const buttons = [mainSubmit, mobileSubmit].filter(Boolean);

    let adding = false;

    const addToCart = async () => {
      const idField = form.querySelector('[name="id"]');
      const qtyField = form.querySelector('[name="quantity"]');

      if (!cartAddUrl || !idField || buttons.length === 0 || adding) return;
      if (mainSubmit?.disabled) return;

      const variantId = Number(idField.value);
      const quantity = Math.max(1, Number(qtyField?.value) || 1);
      if (!variantId) return;

      const addLabel = mainSubmit?.dataset.addLabel || mainSubmit?.textContent || '';
      const addedLabel = mainSubmit?.dataset.addedLabel || addLabel;
      const addingLabel = mainSubmit?.dataset.addingLabel || addLabel;

      adding = true;
      buttons.forEach((button) => {
        button.disabled = true;
        button.textContent = addingLabel;
      });

      try {
        const addResponse = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
        });
        const addPayload = await addResponse.json();
        if (!addResponse.ok) {
          throw new Error(addPayload.description || addPayload.message || 'Could not add to basket');
        }

        if (cartUrl) {
          const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            updateHeaderCartCount(cart.item_count);
          }
        }

        buttons.forEach((button) => {
          button.textContent = addedLabel;
          button.disabled = false;
        });
        window.setTimeout(() => {
          buttons.forEach((button) => {
            button.textContent = addLabel;
          });
        }, 1800);
      } catch (_error) {
        buttons.forEach((button) => {
          button.disabled = mainSubmit?.disabled ?? false;
          button.textContent = addLabel;
        });
      } finally {
        adding = false;
      }
    };

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        addToCart();
      },
      { capture: true }
    );

    buttons.forEach((button) => {
      if (button.dataset.ajaxCartClickBound) return;
      button.dataset.ajaxCartClickBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        addToCart();
      });
    });
  };

  const bindProduct = (scope = document) => {
    scope.querySelectorAll('[data-babaa-product]').forEach((product) => {
      if (product.dataset.bound) return;
      product.dataset.bound = 'true';

      bindAjaxAddToCart(product);

      const lightbox = product.querySelector('[data-product-lightbox]');
      const swiperRoot = lightbox?.querySelector('[data-product-lightbox-swiper]');
      const lightboxCounter = lightbox?.querySelector('[data-product-lightbox-counter]');
      let lightboxTrigger = null;
      let lightboxSwiper = null;

      const formatLightboxIndex = (value) => String(value).padStart(2, '0');

      const updateLightboxCounter = (swiper) => {
        if (!lightboxCounter || !swiper) return;
        const total = swiper.slides.length;
        lightboxCounter.textContent = `${formatLightboxIndex(swiper.activeIndex + 1)}/${formatLightboxIndex(total)}`;
      };

      const ensureLightboxSwiper = () => {
        if (!swiperRoot || lightboxSwiper || typeof Swiper === 'undefined') return lightboxSwiper;

        lightboxSwiper = new Swiper(swiperRoot, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 280,
          keyboard: { enabled: true, onlyInViewport: true },
          navigation: {
            nextEl: lightbox.querySelector('[data-product-lightbox-next]'),
            prevEl: lightbox.querySelector('[data-product-lightbox-prev]'),
          },
          on: {
            init: updateLightboxCounter,
            slideChange: updateLightboxCounter,
          },
        });

        return lightboxSwiper;
      };

      const closeLightbox = () => {
        if (!lightbox) return;
        lightbox.hidden = true;
        document.body.classList.remove('babaa-product-lightbox-open');
        lightboxTrigger?.focus();
        lightboxTrigger = null;
      };

      const openLightbox = (slideIndex, trigger) => {
        if (!lightbox || !swiperRoot) return;

        const swiper = ensureLightboxSwiper();
        if (!swiper) return;

        const index = Number.isFinite(slideIndex) ? slideIndex : 0;
        lightboxTrigger = trigger;
        lightbox.hidden = false;
        document.body.classList.add('babaa-product-lightbox-open');
        swiper.update();
        swiper.slideTo(Math.max(0, index), 0);
        updateLightboxCounter(swiper);
        lightbox.querySelector('.babaa-product-lightbox__close')?.focus();
      };

      if (lightbox) {
        lightbox.querySelectorAll('[data-product-lightbox-close]').forEach((control) => {
          control.addEventListener('click', closeLightbox);
        });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !lightbox.hidden) closeLightbox();
        });
      }

      const infoColumn = product.querySelector('[data-product-info-scroll]');
      if (infoColumn && !infoColumn.dataset.scrollChainBound) {
        infoColumn.dataset.scrollChainBound = 'true';
        infoColumn.addEventListener(
          'wheel',
          (event) => {
            if (isMobileGallery()) return;

            const { scrollHeight, clientHeight, scrollTop } = infoColumn;
            if (scrollHeight <= clientHeight + 1) return;

            const delta = event.deltaY;
            if (!delta) return;

            const maxScroll = scrollHeight - clientHeight;
            const atTop = scrollTop <= 0;
            const atBottom = scrollTop >= maxScroll - 1;

            if ((delta > 0 && atBottom) || (delta < 0 && atTop)) {
              window.scrollBy({ top: delta, left: 0 });
              event.preventDefault();
            }
          },
          { passive: false },
        );
      }

      product.querySelectorAll('[data-product-zoom]').forEach((zoom) => {
        const image = zoom.querySelector('img');
        const sign = zoom.querySelector('.babaa-product__zoom-sign');
        if (!image) return;

        const reset = () => {
          zoom.classList.remove('is-zoomed');
          zoom.setAttribute('aria-pressed', 'false');
          if (sign) sign.textContent = '+';
          image.style.transformOrigin = '50% 50%';
        };

        const origin = (event) => {
          const rect = zoom.getBoundingClientRect();
          const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
          const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
          image.style.transformOrigin = `${x}% ${y}%`;
        };

        zoom.addEventListener('pointermove', (event) => {
          if (isMobileGallery()) return;
          if (event.pointerType === 'mouse') origin(event);
        });
        zoom.addEventListener('pointerleave', () => {
          if (!isMobileGallery()) reset();
        });
        zoom.addEventListener('blur', () => {
          if (!isMobileGallery()) reset();
        });
        zoom.addEventListener('click', (event) => {
          if (isMobileGallery()) {
            event.preventDefault();
            const slideIndex = Number(zoom.getAttribute('data-lightbox-index')) || 0;
            openLightbox(slideIndex, zoom);
            return;
          }

          const active = zoom.classList.toggle('is-zoomed');
          zoom.setAttribute('aria-pressed', String(active));
          if (event.detail > 0) origin(event);
          else image.style.transformOrigin = '50% 50%';
          if (sign) sign.textContent = active ? '−' : '+';
        });
        zoom.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !isMobileGallery()) reset();
        });
      });

      const form = product.querySelector('[data-product-form]');
      const optionSelects = [...product.querySelectorAll('[data-option-select]')];
      const idInput = form?.querySelector('[data-variant-id-input]');
      const variantsNode = product.querySelector('[data-product-variants]');
      const price = product.querySelector('[data-product-price]');
      const comparePrice = product.querySelector('[data-product-compare-price]');
      const submit = product.querySelector('[data-add-to-basket]');
      const skuLine = product.querySelector('[data-product-sku-line]');
      const skuValue = product.querySelector('[data-product-sku]');
      const quantityWrap = product.querySelector('[data-product-quantity-wrap]');

      if (form && optionSelects.length && idInput && variantsNode && price && comparePrice && submit) {
        let variants = [];
        try {
          variants = JSON.parse(variantsNode.textContent);
        } catch (_error) {
          variants = [];
        }

        const optionKeys = ['option1', 'option2', 'option3'];

        const findVariant = () => {
          const selected = optionSelects.map((node) => node.value);
          if (selected.some((value) => !value)) return null;

          return variants.find((item) =>
            optionSelects.every((_node, index) => item[optionKeys[index]] === selected[index]),
          );
        };

        const applyVariant = (variant) => {
          const mobileSubmit = product.querySelector('[data-mobile-purchase]');

          if (!variant) {
            idInput.value = '';
            submit.disabled = true;
            submit.textContent = submit.dataset.addLabel;
            if (quantityWrap) quantityWrap.hidden = true;
            if (mobileSubmit) {
              mobileSubmit.disabled = true;
              mobileSubmit.textContent = submit.textContent;
            }
            return;
          }

          idInput.value = String(variant.id);
          price.textContent = variant.price;
          comparePrice.textContent = variant.compareAtPrice;
          comparePrice.hidden = !variant.compareAtPrice;
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? submit.dataset.addLabel : submit.dataset.soldOutLabel;
          if (quantityWrap) {
            quantityWrap.hidden = !variant.available;
          }
          if (skuLine && skuValue) {
            const sku = variant.sku?.trim?.() ?? variant.sku ?? '';
            if (sku) {
              skuValue.textContent = sku;
              skuLine.hidden = false;
            } else {
              skuValue.textContent = '';
              skuLine.hidden = true;
            }
          }
          if (mobileSubmit) {
            mobileSubmit.disabled = submit.disabled;
            mobileSubmit.textContent = submit.textContent;
          }

          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        };

        const syncVariant = () => applyVariant(findVariant());

        optionSelects.forEach((select) => {
          select.addEventListener('change', syncVariant);
        });

        syncVariant();
      }

      const wrap = product.querySelector('.babaa-product__quantity');
      if (!wrap) return;
      if (wrap.dataset.bound) return;
      wrap.dataset.bound = 'true';

      const input = wrap.querySelector('.babaa-product__qty-input');
      if (!input) return;

      wrap.querySelectorAll('[data-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = Number(btn.getAttribute('data-qty-step')) || 0;
          const min = Number(input.min) || 1;
          const next = Math.max(min, (Number(input.value) || min) + step);
          input.value = String(next);
        });
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindProduct());
  } else {
    bindProduct();
  }

  document.addEventListener('shopify:section:load', (event) => bindProduct(event.target));
})();
